import {
  ForwardedRef,
  forwardRef,
  useMemo,
  useState,
  VideoHTMLAttributes
} from 'react'
import NextImage, { ImageProps as NextImageProps } from 'next/image'

import { cn } from '@/lib/utils'
import { WPImage, WPVideo } from '@/types'

export type MediaElement = {
  use_video: boolean
  image: Partial<WPImage>
  video: Partial<WPVideo>
}

export type ImageProps = Omit<NextImageProps, 'src' | 'alt'> &
  Partial<{
    source: Partial<WPImage>
    size: 'thumbnail' | 'medium' | 'large' | 'full'
    fit: 'contain' | 'cover'
    alt: string
  }>

export type VideoProps = VideoHTMLAttributes<HTMLVideoElement> &
  Partial<{
    source: Partial<WPVideo>
    fit: 'contain' | 'cover'
    fill: boolean
  }>

export type MediaProps = (ImageProps | VideoProps) & Partial<MediaElement>

const Image = forwardRef<HTMLImageElement, ImageProps>(
  (
    { source, size = 'full', fit = 'cover', fill, alt, className, ...props },
    ref
  ) => {
    const [loaded, setLoaded] = useState(false)
    const [src, width, height] = useMemo(
      () => [
        source?.sizes?.[size]?.url || source?.url || '',
        source?.sizes?.[size]?.width || source?.width || 0,
        source?.sizes?.[size]?.height || source?.height || 0
      ],
      [source, size]
    )

    return (
      <NextImage
        ref={ref}
        src={src}
        fill={fill}
        alt={alt || ''}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        draggable={false}
        className={cn(
          'transition-opacity duration-500 ease-in-out-cubic',
          {
            'object-cover object-center': fit === 'cover',
            'object-contain object-center': fit === 'contain'
          },
          {
            'opacity-0': !loaded,
            'opacity-100': loaded
          },
          className
        )}
        {...(!fill ? { width, height } : {})}
        {...props}
      />
    )
  }
)

const Video = forwardRef<HTMLVideoElement, VideoProps>(
  ({ source, fit = 'cover', fill, className, ...props }, ref) => {
    return (
      <video
        ref={ref}
        playsInline={true}
        autoPlay={true}
        loop={true}
        muted={true}
        className={cn(
          'relative',
          {
            'absolute left-0 top-0 h-full w-full': fill
          },
          {
            'object-cover object-center': fit === 'cover',
            'object-contain object-center': fit === 'contain'
          },
          className
        )}
        {...props}
      >
        <source src={source?.url} type="video/mp4" />
      </video>
    )
  }
)

const Media = forwardRef<HTMLImageElement | HTMLVideoElement, MediaProps>(
  ({ use_video, image, video, ...props }, ref) => {
    return use_video ? (
      <Video
        ref={ref as ForwardedRef<HTMLVideoElement>}
        source={video}
        {...(props as VideoProps)}
      />
    ) : (
      <Image
        ref={ref as ForwardedRef<HTMLImageElement>}
        source={image}
        alt={image?.alt}
        {...(props as ImageProps)}
      />
    )
  }
)

export default Media
